"use client"

import { useRef, useEffect, useState, useCallback } from "react"
import { ArrowDown, Wrench } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { ChatMessage } from "./chat-message"
import { ToolResultCard } from "./tool-result-card"
import { StreamingStatus } from "./thinking-indicator"
import type { ChatMessage as ChatMessageType } from "@/lib/ai-chat/types"

interface StreamingToolCall {
  toolName: string
  /** Server-authored human label for `toolName`, when the backend sent one. */
  toolLabel?: string
  status: "executing" | "Completed" | "error"
  success?: boolean
  summary?: string
}

interface ChatMessageListProps {
  messages: ChatMessageType[]
  streamingContent: string
  isStreaming: boolean
  streamingToolCalls: StreamingToolCall[]
  onRegenerate: () => void
  onEditMessage: (messageId: string) => void
  compact?: boolean
  conversationId?: string | null
}

export function ChatMessageList({
  messages,
  streamingContent,
  isStreaming,
  streamingToolCalls,
  onRegenerate,
  onEditMessage,
  compact,
  conversationId,
}: ChatMessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Whether the reader is parked at the bottom and therefore wants to follow
  // new output. Previously the list scrolled to the bottom on EVERY streamed
  // token with no escape hatch, so anyone scrolling up to re-read an earlier
  // answer was yanked back down on the next chunk.
  const followOutputRef = useRef(true)

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    followOutputRef.current = true
    setShowScrollButton(false)
    bottomRef.current?.scrollIntoView({ behavior })
  }, [])

  // Sending re-arms follow mode: the user asked for this answer, so jump back
  // down even if they had scrolled up to read history first. MUST be declared
  // before the auto-scroll effect — effects fire in declaration order, and the
  // re-arm has to land before the same commit's scroll decision reads the flag.
  const lastUserMessageId = messages.findLast((m) => m.role === "user")?.id
  useEffect(() => {
    if (!lastUserMessageId) return
    // Ref only — the scroll that follows fires a scroll event, and the
    // position tracker below is what hides the FAB.
    followOutputRef.current = true
  }, [lastUserMessageId])

  // Auto-scroll on new content — only while the reader has not scrolled away.
  // `auto` (not `smooth`) while streaming: a smooth scroll queued per token
  // never settles, which is what made the viewport feel like it was fighting
  // the user even when they were at the bottom.
  useEffect(() => {
    if (!followOutputRef.current) return
    bottomRef.current?.scrollIntoView({ behavior: isStreaming ? "auto" : "smooth" })
  }, [messages.length, streamingContent, isStreaming])

  // Track the reader's position — drives both the follow flag and the FAB.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return

    const handleScroll = () => {
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100
      followOutputRef.current = isNearBottom
      setShowScrollButton(!isNearBottom)
    }

    el.addEventListener("scroll", handleScroll)
    return () => el.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden relative min-w-0">
      <div className={cn("flex flex-col min-w-0 w-full", compact ? "py-2" : "py-4")}>
        {messages.map((msg, i) => (
          <ChatMessage
            key={msg.id}
            message={msg}
            isLast={i === messages.length - 1 && !isStreaming}
            onRegenerate={!isStreaming && i === messages.length - 1 ? onRegenerate : undefined}
            onEdit={!isStreaming && msg.role === "user" ? () => onEditMessage(msg.id) : undefined}
            compact={compact}
            conversationId={conversationId}
          />
        ))}

        {/* Streaming: thinking indicator + tool calls + text */}
        {isStreaming && (
          <>
            {/* Show thinking indicator when AI is working but no text yet */}
            {!streamingContent && streamingToolCalls.length === 0 && (
              <StreamingStatus isThinking compact={compact} />
            )}

            {/* Tool calls — show inline status with animated indicator */}
            {streamingToolCalls.length > 0 && (
              <div className={cn("py-1 min-w-0", compact ? "px-2 ms-7" : "px-4 ms-11")}>
                {/* Completed tools count */}
                {streamingToolCalls.filter(tc => tc.status !== "executing").length > 0 && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-0.5">
                    <Wrench className="h-3 w-3 shrink-0" />
                    <span>
                      {streamingToolCalls.filter(tc => tc.status !== "executing").length} tool{streamingToolCalls.filter(tc => tc.status !== "executing").length > 1 ? "s" : ""} completed
                    </span>
                  </div>
                )}
                {/* Currently executing tool — show with animated status */}
                {streamingToolCalls.filter(tc => tc.status === "executing").map((tc, i) => (
                  <ToolResultCard
                    key={`${tc.toolName}-${i}`}
                    toolName={tc.toolName}
                    toolLabel={tc.toolLabel}
                    status={tc.status}
                    success={tc.success}
                    summary={tc.summary}
                  />
                ))}
                {/* Show thinking below tools when waiting for more */}
                {streamingToolCalls.every(tc => tc.status !== "executing") && !streamingContent && (
                  <StreamingStatus isThinking compact={compact} />
                )}
              </div>
            )}

            {/* Streaming assistant message */}
            {streamingContent && (
              <ChatMessage
                message={{
                  id: "streaming",
                  role: "assistant",
                  content: streamingContent,
                  createdAt: new Date().toISOString(),
                }}
                isLast
                isStreaming
                compact={compact}
              />
            )}
          </>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Scroll to bottom FAB */}
      {showScrollButton && (
        <Button
          variant="outline"
          size="icon"
          className="absolute bottom-4 end-4 h-8 w-8 rounded-full shadow-md bg-background/90 backdrop-blur-sm"
          onClick={() => scrollToBottom()}
          aria-label="Scroll to latest"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      )}
    </div>
  )
}
